import clsx from 'clsx'
import { Box, makeStyles, Typography } from '@material-ui/core'
import { memo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { updatedPlantInfo } from '@/slices/hydro/plantView'
import { europeNum, getDeviceLogs, getExcelLogs } from '@utils/general'
import moment from 'moment'
import fileDownload from 'js-file-download'
import LoadingCard from '@components/LoadingCard'
import { basicColors } from '@theme'
import DeviceChip from './DeviceChip'
import DownloadDatasheetsDialog from '../../shared/DownloadDatasheetsDialog'
import ModalExcelDeviceLogs from '@components/ModalExcelDeviceLogs'
import ModalDeviceLogs from '@components/ModalDeviceLogs'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'

const useStyles = makeStyles(() => ({
  root: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  errorText: {
    color: 'white',
    textAlign: 'center'
  },
  cardLabel: {
    color: basicColors.blueGrayLight
  }
}))

// mi preparo i dati per i log
const logsParams = {
  deviceId: '',
  name: [],
  timeTo: null,
  timeFrom: null
}

function Components ({ className, ...rest }) {
  const classes = useStyles()

  const { uuid: plantId, hasError, devices, dataloggers } = useSelector((state) => state.hydroPlantView)
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const enabledDeviceModels = ['RittmeyerFlow']
  const enabledDeviceCategories = ['EnergyMeter']

  const enabledDevices = devices.filter(device => enabledDeviceCategories.includes(device.deviceType?.category) || enabledDeviceModels.includes(device.deviceType?.model))

  const now = moment()
    .set({ minute: 0, second: 0, millisecond: 0 })
    .toISOString()

  const [showDownloadDialog, setShowDownloadDialog] = useState(false)
  const [currentDocuments, setCurrentDocuments] = useState(null)

  // Variabili per le dialog dei logs
  const [openLogsModal, setOpenLogsModal] = useState(false)
  const [openExcelModal, setOpenExcelModal] = useState(false)
  const [logs, setLogs] = useState({
    uuid: '',
    name: '',
    data: {},
    columns: [],
    rows: []
  })
  const [isExcelTooBig, setIsExcelTooBig] = useState(false)
  const [from, setFrom] = useState(moment(now).toISOString())
  const [to, setTo] = useState(
    moment(now)
      .add({ hours: '1' })
      .toISOString()
  )

  useEffect(() => {
    if (plantId && plantId !== 'demo') {
      dispatch(updatedPlantInfo({ plantId }))
    }

    const overviewInterval = setInterval(() => {
      dispatch(updatedPlantInfo({ plantId }))
    }, 60000)
    return () => {
      clearInterval(overviewInterval)
    }
  }, [dispatch, plantId, hasError])

  const devicesRows = enabledDevices.map((device) => {
    // console.log('rows: ', inverter)
    // const datasheetUrl = inverter.metadata && inverter.metadata.documents && inverter.metadata.documents.length > 0 ? inverter.metadata.documents[inverter.metadata.documents.length - 1].url : ''
    const inverterDocuments = device?.metadata?.documents ?? []
    const deviceTypeDocuments = device.deviceType?.metadata?.documents ?? []
    let documents = deviceTypeDocuments.concat(inverterDocuments)

    if (documents.length === 0) {
      documents = null
    }

    const currentRow = {
      uuid: device.uuid,
      type: 'device',
      name: device.name,
      model: device?.deviceType?.model,
      lastMex: device?.lastCommunicationAt !== 'never' ? format(new Date(device?.lastCommunicationAt), 'dd/MM/yy HH:mm') : device?.lastActivityAt !== 'never' ? format(new Date(device?.lastActivityAt), 'dd/MM/yy HH:mm') : 'N.F',
      status: {
        general: null,
        communication: null
      },
      datalogger: 'N.F.',
      actions: {
        documents
      }
    }

    Object.keys(device?.deviceType.properties).forEach(key => {
      if (!currentRow[key]) {
        currentRow[key] = 'N.F.'
      }
    })

    // cerco il datalogger
    const dataloggerIndex = dataloggers.findIndex((datalogger) => datalogger.uuid === device.nodeId)
    if (dataloggerIndex > -1) {
      currentRow.datalogger = dataloggers[dataloggerIndex].name
    }
    // controllo se ho dati
    if (device.state && Object.keys(device.state).length > 0) {
      // controllo i dati che ho
      // comunicazione e stato
      if (Object.prototype.hasOwnProperty.call(device, 'lastActivityAt') && device.lastActivityAt !== 'never') {
        // controllo la differenza del periodo
        const a = moment()
        const b = moment(device.lastActivityAt)
        if (a.diff(b, 'minutes') >= 30) {
          currentRow.status.communication = 'offline'
        } else {
          currentRow.status.communication = 'online'
        }
      } else {
        currentRow.status.communication = 'offline'
      }
      if (Object.prototype.hasOwnProperty.call(device.metadata, 'status')) {
        currentRow.status.general = device.metadata.status
      } else {
        currentRow.status.general = 'ok'
      }
      // altri dati
      Object.keys(device.state ?? {}).forEach(key => {
        if (currentRow[key] && device.state[key]) {
          currentRow[key] = europeNum(device.state[key], 2)
        }
      })
    }

    return currentRow
  })

  // funzione che torna l'array con le colonne
  const generateLogColumns = (deviceProps, props) => {
    const arrayToSend = []
    if (deviceProps) {
      Object.keys(deviceProps).forEach(key => {
        const label = deviceProps[key]?.uom ? `${deviceProps[key]?.displayName || '-'} (${deviceProps[key].uom})` : `${deviceProps[key]?.displayName || '-'}`
        const propObject = {
          prop: key,
          label
        }
        arrayToSend.push(propObject)
      })
    }

    return arrayToSend
  }

  // Funzione che preso in ingresso l'oggetto dei logs di un singolo device restituisce lo stesso oggetto ma trascurando la diversità di secondi tra un messaggio e l'altro
  const approximateLogsToMinutesPrecision = (logsObj) => {
    if (logsObj) {
      return Object.keys(logsObj).reduce((acc, propKey) => {
        const newProp = logsObj[propKey]
          .map(dateValueEl => dateValueEl?.map((el, index) => index === 0 ? moment(el).format('DD/MM/YYYY HH:mm') : el))

        acc[propKey] = newProp

        return acc
      }, {})
    }

    return {}
  }

  // funzione che apre la modal dei log
  const openLogsDialog = async (row) => {
    // setto i valori nei parametri della query
    logsParams.deviceId = row.uuid
    // mi calcolo le giuste propsLabels
    const currentDevice = devices.find(device => device.uuid === row.uuid)
    const newColumns = generateLogColumns(currentDevice.deviceType?.properties, currentDevice ? currentDevice.state : null)

    logsParams.name = newColumns.map((col) => col.prop)
    logsParams.timeFrom = from
    logsParams.timeTo = to
    const newData = await getDeviceLogs(logsParams)
    // mi preparo le righe
    const formattedData = approximateLogsToMinutesPrecision(newData[currentDevice?.uuid] || {})
    const newRows = generateLogRows(formattedData)

    setLogs({
      uuid: logsParams.deviceId,
      name: row.name,
      columns: newColumns,
      rows: newRows,
      data: formattedData
    })
    // console.log('logs => ', logs)

    setOpenLogsModal(true)
  }

  // funzione che viene invocata alla chiusura della modal dei log
  const handleCloseLogsModal = () => {
    setOpenLogsModal(false)
  }

  // funzione che ritorna l'array delle righe
  const generateLogRows = (data) => {
    const arrayToReturn = []
    // mi scorro i valori di tutte le prop e mi salvo i valori ISOString che non ho
    Object.keys(data).forEach((key) => {
      data[key].forEach((elem) => {
        if (arrayToReturn.indexOf(elem[0]) === -1) {
          arrayToReturn.push(elem[0])
        }
      })
    })
    // Ordino l'array in base al tempo (dal più vicino al from fino al to)
    arrayToReturn.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    return arrayToReturn
  }

  // funzione che aggiorna i dati
  const onPeriodChange = async (newFrom, newTo) => {
    // console.log('onPeriodChange, newFrom => ', newFrom)
    // console.log('onPeriodChange, newTo => ', newTo)
    logsParams.timeFrom = moment(newFrom).toISOString()
    logsParams.timeTo = moment(newTo).toISOString()
    setFrom(logsParams.timeFrom)
    setTo(logsParams.timeTo)

    const newData = await getDeviceLogs(logsParams)

    const key = Object.keys(newData)
    const formattedData = approximateLogsToMinutesPrecision(newData[key])
    const newRows = generateLogRows(formattedData)

    setLogs({ ...logs, data: formattedData, rows: newRows })
    // console.log('logs => ', logs)
  }

  // funzione che apre la modal per scaricare Excel
  const toogleDownloadExcel = () => {
    setOpenExcelModal(true)
  }

  // funzione che viene invocata alla chiusura della modal dei log
  const handleCloseExcelModal = () => {
    setOpenExcelModal(false)
  }

  // funzione che scarica l'excel
  const downloadExcel = async (from, to, selectedAggregation) => {
    setIsExcelTooBig(false)
    // console.log('downloadExcel from => ', from)
    // console.log('downloadExcel to => ', to)
    const excelParams = JSON.parse(JSON.stringify(logsParams))
    excelParams.timeFrom = moment(from).toISOString()
    excelParams.timeTo = moment(to).toISOString()
    excelParams.aggregationType = selectedAggregation || 'quarter'
    // console.log('excelParams => ', excelParams)
    try {
      const excelResponse = await getExcelLogs(excelParams)
      // console.log('excelResponse => ', excelResponse)
      // controllo se è ritornato il file o se manda email
      if (excelResponse.status === 202) {
        // mando l'excel per email
        // console.log('MANDO LA MAILLLLL')
        setIsExcelTooBig(true)
      } else {
        // scarico il blob
        fileDownload(
          excelResponse.data,
          `LOGS ${logs.name} - dal ${moment(from).format('DD-MM-YY (HH)')} al ${moment(to).format(
            'DD-MM-YY (HH)'
          )} - dati tipo ${selectedAggregation || 'quarter'}.xlsx`
        )
      }
    } catch (err) {
      console.error('get excel err => ', err)
      if (String(err).indexOf('404') > -1) {
        enqueueSnackbar('Attenzione: non ci sono dati per il tipo di aggregazione richiesta.', {
          variant: 'warning'
        })
      } else {
        enqueueSnackbar('Errore nel richiedere i log in Excel, per favore aggiornare la pagina e riprovare.', {
          variant: 'error'
        })
      }
    }
  }

  return (
    <>
      {showDownloadDialog
        ? <DownloadDatasheetsDialog list={currentDocuments} open={showDownloadDialog} onClose={() => setShowDownloadDialog(false)} />
        : null}
      <Box px={2} py={1} className={clsx(classes.root, className)}>
        {dataloggers[0] === '-'
          ? <LoadingCard glass />
          : dataloggers.length === 0
            ? <Typography variant='body1' className={classes.errorText}>Non ci sono dispositivi per questo impianto</Typography>
            : (
              <>
                <Typography className={classes.cardLabel} variant='caption' component='div'>Componenti</Typography>
                <Box flexWrap='wrap' display='flex' alignItems='center' mt={0.5} width='100%'>
                  {devicesRows?.map(el => (
                    <DeviceChip
                      onClick={openLogsDialog}
                      key={el.uuid}
                      deviceProps={enabledDevices.find(device => device.uuid === el.uuid)?.deviceType?.properties ?? {}}
                      device={el}
                      downloadActions={(documents) => {
                        setShowDownloadDialog(true)
                        setCurrentDocuments(documents || [])
                      }}
                    />))}
                </Box>
                {openLogsModal
                  ? (
                    <ModalDeviceLogs
                      open={openLogsModal}
                      onClose={handleCloseLogsModal}
                      data={logs.data}
                      columnsLabels={logs.columns}
                      initialRows={logs.rows}
                      name={logs.name}
                      onPeriodChange={onPeriodChange}
                      initialFrom={from}
                      initialTo={to}
                      toogleDownloadExcel={toogleDownloadExcel}
                    />)
                  : null}
                {openExcelModal
                  ? (
                    <ModalExcelDeviceLogs
                      open={openExcelModal}
                      onClose={handleCloseExcelModal}
                      deviceId={logs.uuid}
                      name={logs.name}
                      initialFrom={from}
                      initialTo={to}
                      downloadExcel={downloadExcel}
                      isExcelTooBig={isExcelTooBig}
                    />)
                  : null}

              </>)}
      </Box>
    </>
  )
}
export default memo(Components)
